// "What Hush has learned" (Settings): how it files each merchant, which alerts it merges with
// entries, and which merchants pay monthly items. Change a merchant's category or forget it;
// forgetting changes only what happens next, never past purchases.
import type { LearnedDto, OverviewDto } from '../../../shared/api';
import { dayLabel, esc } from '../format';
import { detailBar } from './header';
import { spendingCategories } from './items';

export const LEARNED_INTRO = 'Hush remembers how you file purchases so it can do it for you next time.';

/** "Used 4 times, last on Oct 3" / "Not used yet". */
export function usesText(uses: number, lastUsed: string | null): string {
  if (!uses) return 'Not used yet';
  const times = uses === 1 ? 'Used once' : uses === 2 ? 'Used twice' : `Used ${uses} times`;
  return lastUsed ? `${times}, last on ${dayLabel(lastUsed)}` : times;
}

/** Lowercased text the search box matches against. */
const haystack = (...parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' ').toLowerCase();

export function vLearned(d: OverviewDto, l: LearnedDto | null, search = ''): string {
  const head = `${detailBar('What Hush has learned', 'Change or forget anything here')}<p class="hint">${esc(LEARNED_INTRO)}</p>`;
  if (!l) return `${head}<p class="empty">Loading...</p>`;
  const cats = spendingCategories(d);
  const rules = l.rules.length
    ? `<div class="list learned">${l.rules
        .map(
          (r) => `<div class="lrow" data-learn-row="${esc(haystack(r.name, r.category, r.item?.name))}">
      <div class="lmain"><b>${esc(r.name)}</b><span class="hint">${esc(r.item ? `${r.category}, ${r.item.name} item` : r.category)}. ${esc(usesText(r.uses, r.lastUsed))}</span></div>
      <div class="lacts"><select data-learn-rule="${esc(r.key)}" aria-label="Category for ${esc(r.name)}">${cats
        .map((k) => `<option value="${esc(k.id)}" ${k.id === r.categoryId ? 'selected' : ''}>${esc(k.name)}</option>`)
        .join('')}</select><button class="btn" data-a="learn-forget" data-key="${esc(r.key)}" data-name="${esc(r.name)}">Forget</button></div></div>`,
        )
        .join('')}</div>`
    : '<p class="hint">Nothing yet. Each time you file a purchase, Hush remembers the merchant.</p>';
  const merges = l.merges.length
    ? `<h2>Matched to your own entries</h2><div class="list learned">${l.merges
        .map(
          (m) => `<div class="lrow" data-learn-row="${esc(haystack(m.name, m.target))}"><div class="lmain"><b>${esc(m.name)}</b><span class="hint">${esc(
            m.same ? `Same as ${m.target}: merged with your entry.` : `Not the same as ${m.target}: kept as two purchases.`,
          )}</span></div><div class="lacts"><button class="btn" data-a="learn-forget-merge" data-key="${esc(m.key)}">Forget</button></div></div>`,
        )
        .join('')}</div>`
    : '';
  const monthly = l.monthly.length
    ? `<h2>Monthly charges</h2><div class="list learned">${l.monthly
        .map(
          (m) => `<div class="lrow" data-learn-row="${esc(haystack(m.name, m.item))}"><div class="lmain"><b>${esc(m.name)}</b><span class="hint">${esc(`Pays your ${m.item} item.`)}</span></div>
      <div class="lacts"><button class="btn" data-a="learn-forget-monthly" data-key="${esc(m.key)}" data-id="${esc(m.itemId)}">Forget</button></div></div>`,
        )
        .join('')}</div>`
    : '';
  const any = l.rules.length + l.merges.length + l.monthly.length > 0;
  return `${head}
  ${any ? `<label class="fld"><span>Search</span><input id="learn-search" type="search" placeholder="Merchant or category" value="${esc(search)}"></label>` : ''}
  <h2>Merchants</h2>${rules}${merges}${monthly}
  <p class="hint">Forget: the next purchase from that merchant asks again. Purchases already filed stay where they are.</p>`;
}
